import { useEffect, useRef } from 'react';

function useScrollAnimation(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible');
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
}

const accessInfo = [
  { icon: 'ri-map-pin-line', label: '住所', value: '〒101-0041\n東京都千代田区神田須田町2-6-20\nマリアナ神田須田町 2F' },
  {
    icon: 'ri-train-line',
    label: '最寄り駅',
    value: 'JR秋葉原駅 徒歩5分\nJR神田駅 徒歩5分\n岩本町駅 徒歩1分',
  },
  { icon: 'ri-time-line', label: '営業時間', value: '24時間 年中無休' },
  { icon: 'ri-lock-2-line', label: '入退室', value: 'スマートロックによる自動入退室\n予約完了後に暗証番号をご案内' },
];

export default function Access() {
  const titleRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);

  useScrollAnimation(titleRef as React.RefObject<HTMLElement>);
  useScrollAnimation(mapRef as React.RefObject<HTMLElement>);
  useScrollAnimation(infoRef as React.RefObject<HTMLElement>);

  return (
    <section id="access" className="bg-[#0f1419] py-20 md:py-28">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div ref={titleRef} className="fade-in-section text-center mb-14">
          <p className="section-label mb-3">ACCESS</p>
          <h2 className="font-playfair font-bold text-white text-3xl md:text-4xl mb-4">
            アクセス
          </h2>
          <p className="text-white/50 font-noto text-sm md:text-base">
            秋葉原・神田の2駅からそれぞれ徒歩5分以内。都内各地からアクセス便利です。
          </p>
        </div>

        {/* Map */}
        <div
          ref={mapRef}
          className="fade-in-section rounded-2xl overflow-hidden mb-8 border border-[#2a2f3e]"
          style={{ height: '420px' }}
        >
          <iframe
            title="ポーカーレンタルスペース デュエル アクセスマップ"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1926.6648940970233!2d139.77198811016405!3d35.695435413482805!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188ea9d1cf573d%3A0xa2e4820473c70213!2z44CSMTAxLTAwNDEg5p2x5Lqs6YO95Y2D5Luj55Sw5Yy656We55Sw6aCI55Sw55S677yS5LiB55uu77yW4oiS77yS77yQ!5e0!3m2!1sja!2sjp!4v1776259599225!5m2!1sja!2sjp"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        {/* Info cards */}
        <div
          ref={infoRef}
          className="fade-in-section grid grid-cols-1 sm:grid-cols-2 gap-4"
          style={{ transitionDelay: '150ms' }}
        >
          {accessInfo.map((info) => (
            <div
              key={info.label}
              className="bg-[#1a1f2e] rounded-2xl p-6 border border-[#2a2f3e] flex items-start gap-4"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-[#d4af37]/10 rounded-xl shrink-0">
                <i className={`${info.icon} text-[#d4af37] text-xl`} />
              </div>
              <div>
                <p className="text-[#d4af37] text-xs font-semibold tracking-wider mb-1 uppercase">
                  {info.label}
                </p>
                <p className="text-white font-noto text-sm leading-relaxed whitespace-pre-line">
                  {info.value}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Direction button */}
        <div className="mt-8 text-center">
          <a
            href="https://maps.google.com/?q=東京都千代田区神田須田町2-6-20-2F"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3 border border-[#d4af37]/50 text-[#d4af37] rounded-full hover:bg-[#d4af37]/10 transition-all duration-200 text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            <i className="ri-map-2-line text-base" />
            Googleマップで経路を確認
          </a>
        </div>
      </div>
    </section>
  );
}
